import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import styled from '@emotion/styled';
import { loadStripe, Stripe, StripeElements, StripePaymentElement } from '@stripe/stripe-js';
import { paymentStore } from '@/stores/PaymentStore';
import { subscriptionStore } from '@/stores/SubscriptionStore';
import { apiStore } from '@/stores/ApiStore';
import { config } from '@/config/env';
import { getErrorMessage } from '@/hooks/usePaymentErrors';
import { sendPaymentFailed, sendPaymentSuccess, isInWebView } from '@/hooks/useWebViewMessaging';
import { AccountLayout } from '@/components/ui/AccountLayout';
import { PaywallHeader } from '@/components/paywall/PaywallHeader';
import { CtaButton } from '@/components/ui/CtaButton';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { ROUTES } from '@/constants/routes';
import {
  buildAppsFlyerDeeplinkFromPath,
  buildAppsFlyerRedirectUrl,
  type PaymentHandoffMethod,
} from '@/utils/deeplink';
import { consumeAppsFlyerParams } from '@/utils/appsFlyerParams';
import type { SubscriptionProcessResponse, SubscriptionStatus } from '@/types/api';

const WrapTitle = styled.div`
  margin-bottom: 28px;
`;

const ErrorState = styled.div`
  text-align: center;
  padding: 40px 0;

  p {
    margin-bottom: 20px;
    color: #666;
  }
`;

const PaymentContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const PaymentForm = styled.div`
  flex: 1;
`;

const CardSection = styled.div`
  margin-bottom: 24px;
`;

const CardForm = styled.div`
  margin-top: 8px;
  position: relative;
  min-height: 60px;
`;

const PaymentElement = styled.div`
  /* Stripe payment element will be mounted here */
`;

const LoadingSpinnerWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 10;
`;

const CardError = styled.div`
  color: #ef4444;
  font-size: 12px;
  margin-top: 8px;
`;

const ContentBottom = styled.div`
  margin-top: auto;
  padding: 24px 0;
`;

const CtaSection = styled.section`
  margin-bottom: 0;
`;

const isSubscriptionProcessResponse = (
  response: SubscriptionStatus | SubscriptionProcessResponse,
): response is SubscriptionProcessResponse => typeof response.status === 'object';

export const PaymentPage = observer(() => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cardError, setCardError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paymentMethodSelected, setPaymentMethodSelected] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const paymentElementRef = useRef<StripePaymentElement | null>(null);
  const setupElementsRef = useRef<StripeElements | null>(null);
  const stripeRef = useRef<Stripe | null>(null);
  const savedPaymentMethodRef = useRef<string | null>(null);
  const selectedPaymentMethodRef = useRef<PaymentHandoffMethod>('card');

  useEffect(() => {
    const init = async () => {
      if (!apiStore.isAuthenticated) {
        setInitializing(false);
        navigate(ROUTES.AUTH.LOGIN);
        return;
      }

      const currentUser = apiStore.user ?? await apiStore.getMe();
      if (!currentUser) {
        setInitializing(false);
        navigate(ROUTES.AUTH.LOGIN, { replace: true });
        return;
      }
      if (currentUser.checkoutMode === 'session') {
        setInitializing(false);
        navigate(ROUTES.ACCOUNT.SUBSCRIPTION, { replace: true });
        return;
      }

      await subscriptionStore.fetchStatus();
      if (subscriptionStore.hasAccess) {
        setInitializing(false);
        navigate(ROUTES.ACCOUNT.SUBSCRIPTION);
        return;
      }

      const planId = searchParams.get('planId');
      if (planId) paymentStore.setPlanId(planId);

      if (!subscriptionStore.plans || subscriptionStore.plans.length === 0) {
        await subscriptionStore.fetchPlans();
      }

      try {
        await initializeStripeElements();
      }
      catch (error) {
        console.error('Failed to initialize Stripe:', error);
        setInitializing(false);
      }
    };

    void init();

    return () => {
      if (paymentElementRef.current) {
        paymentElementRef.current.unmount();
      }
    };
  }, [navigate, searchParams]);

  const initializeStripeElements = async () => {
    const stripe = await loadStripe(config.stripePublishableKey);
    if (!stripe) throw new Error('Stripe not loaded');

    stripeRef.current = stripe;

    // Clear previous elements if they exist
    if (paymentElementRef.current) {
      paymentElementRef.current.unmount();
      paymentElementRef.current = null;
    }

    const { setup } = await apiStore.initPaymentMethodSelect();
    if (!setup.clientToken) throw new Error('Failed to get client token');

    const setupElements = stripe.elements({
      clientSecret: setup.clientToken,
      customerSessionClientSecret: setup.sessionToken,
    });

    setupElementsRef.current = setupElements;

    // Create Stripe payment element
    const paymentElement = setupElements.create('payment', {
      layout: {
        type: 'tabs',
        defaultCollapsed: false,
        radios: false,
        spacedAccordionItems: false,
      },
    });

    paymentElementRef.current = paymentElement;

    // Event handlers
    paymentElement.on('change', (event) => {
      setCardError('');

      const isWallet = event.value?.type && ['apple_pay'].includes(event.value.type);
      selectedPaymentMethodRef.current = isWallet ? 'apple_pay' : 'card';
      setPaymentMethodSelected(isWallet || (event.complete && !event.empty));
    });

    paymentElement.on('focus', () => setCardError(''));
    paymentElement.on('ready', () => setCardError(''));

    // Wait for DOM element
    await new Promise((resolve) => setTimeout(resolve, 100));
    const element = document.getElementById('payment-element');
    if (!element) {
      throw new Error('Payment element not found in DOM');
    }

    paymentElement.mount('#payment-element');
    setInitializing(false);
  };

  const currentPlan = subscriptionStore.plans?.find(
    (plan) => plan.planId === paymentStore.selectedPlanId,
  );

  const isFormValid = !initializing && paymentMethodSelected && paymentStore.selectedPlanId && currentPlan;

  const getSubscribeButtonText = () => {
    if (!currentPlan) return 'No plan selected';
    console.log('currentPlan', currentPlan);
    const price = currentPlan.discount?.discountedPrice || currentPlan.price;
    return `Subscribe for $${price}`;
  };

  const handleSubscribe = async () => {
    try {
      setProcessing(true);

      if (!currentPlan || !setupElementsRef.current || !stripeRef.current) {
        throw new Error('Payment not ready');
      }

      await subscriptionStore.fetchStatus(true);
      if (subscriptionStore.hasAccess) {
        navigate(ROUTES.ACCOUNT.SUBSCRIPTION);
        return;
      }

      // Step 1: Submit payment form data
      const { error: submitError } = await setupElementsRef.current.submit();
      if (submitError) {
        throw new Error(
          submitError instanceof Error ? submitError.message : String(submitError?.message ?? 'Payment failed'),
        );
      }

      // Step 2: Confirm setup with Stripe
      const setupResult = await stripeRef.current.confirmSetup({
        elements: setupElementsRef.current,
        confirmParams: { return_url: window.location.href },
        redirect: 'if_required',
      });

      if (setupResult.error) {
        // Handle setup_intent_unexpected_state error
        if (setupResult.error.code === 'setup_intent_unexpected_state') {
          console.warn('SetupIntent in unexpected state, reinitializing...');
          await initializeStripeElements();
          setCardError('Please try again. Payment form has been refreshed.');
          return;
        }
        throw new Error(
          setupResult.error instanceof Error
            ? setupResult.error.message
            : String(setupResult.error?.message ?? 'Setup failed'),
        );
      }

      const setupIntent = setupResult.setupIntent;
      if (!setupIntent?.payment_method) {
        throw new Error('Failed to save payment method');
      }

      // Get saved payment method ID
      savedPaymentMethodRef.current
        = typeof setupIntent.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent.payment_method.id;

      if (!savedPaymentMethodRef.current) {
        throw new Error('Payment method ID not available');
      }

      // Step 3: Create subscription with saved payment method
      const response = await apiStore.createSubscription({
        planId: paymentStore.selectedPlanId,
        methodData: { paymentMethodId: savedPaymentMethodRef.current },
      });

      if (!response) {
        throw new Error('Failed to create subscription');
      }

      const subscription = isSubscriptionProcessResponse(response)
        ? response.status
        : response;
      const clientToken = isSubscriptionProcessResponse(response)
        ? response.clientToken
        : undefined;

      // Step 4: Confirm payment via Stripe if needed
      if (subscription.status === 'incomplete') {
        if (!clientToken) {
          throw new Error('Payment confirmation token is missing');
        }
        await setupElementsRef.current.submit();
        const { error } = await stripeRef.current.confirmPayment({
          elements: setupElementsRef.current,
          clientSecret: clientToken,
          confirmParams: { return_url: window.location.href },
          redirect: 'if_required',
        });

        if (error) {
          setCardError(getErrorMessage(error));
        }
      }

      // Check subscription status
      if (!subscription.hasAccess) {
        return;
      }

      // Update subscription status and complete process
      await subscriptionStore.fetchStatus(true);

      // Send payment success message
      sendPaymentSuccess(
        selectedPaymentMethodRef.current === 'apple_pay' ? 'applepay' : 'card',
      );

      if (isInWebView()) {
        return;
      }

      const appsFlyerParams = consumeAppsFlyerParams();

      try {
        // Создаем handoff токен для редиректа
        const handoffResult = await apiStore.createHandoff();
        if (handoffResult.ok && handoffResult.token) {
          // Получаем текущий путь (страница payment)
          const currentPath = window.location.pathname;
          // Формируем диплинк (без paywall версии, так как это оплата из личного кабинета)
          const appsFlyerDeeplink = buildAppsFlyerDeeplinkFromPath(
            currentPath,
            {
              handoffToken: handoffResult.token,
              includePaywall: false,
              paymentMethod: selectedPaymentMethodRef.current,
            },
          );
          const redirectUrl = buildAppsFlyerRedirectUrl(
            appsFlyerParams,
            appsFlyerDeeplink,
            'account_payment',
          );
          window.location.href = redirectUrl;
          return;
        }
      }
      catch (error) {
        console.error('Failed to create handoff for AppsFlyer redirect:', error);
        // Fallback на обычную навигацию если handoff не удался
      }

      // Navigate to subscription page as browser fallback.
      navigate(ROUTES.ACCOUNT.SUBSCRIPTION);
    }
    catch (error) {
      const errorMessage = getErrorMessage(error);
      setCardError(errorMessage);
      sendPaymentFailed(errorMessage);
    }
    finally {
      setProcessing(false);
    }
  };

  const backToPlans = () => {
    navigate(ROUTES.ACCOUNT.SUBSCRIPTION);
  };

  const toGear = () => {
    navigate(ROUTES.POLICY.INDEX);
  };

  if (!paymentStore.selectedPlanId && !initializing) {
    return (
      <AccountLayout showLogo showGear showHome onBack={toGear}>
        <WrapTitle>
          <PaywallHeader>Payment details</PaywallHeader>
        </WrapTitle>
        <ErrorState>
          <p>No plan selected. Please go back and select a plan.</p>
          <CtaButton onClick={backToPlans}>Back to Plans</CtaButton>
        </ErrorState>
      </AccountLayout>
    );
  }

  return (
    <AccountLayout showLogo showGear showHome onBack={toGear}>
      <WrapTitle>
        <PaywallHeader>Payment details</PaywallHeader>
      </WrapTitle>

      <PaymentContent>
        <PaymentForm>
          <CardSection>
            <CardForm>
              {initializing && (
                <LoadingSpinnerWrapper>
                  <LoadingSpinner />
                </LoadingSpinnerWrapper>
              )}
              <PaymentElement id="payment-element" />
              {cardError && <CardError>{cardError}</CardError>}
            </CardForm>
          </CardSection>
        </PaymentForm>

        {!initializing && (
          <ContentBottom>
            <CtaSection>
              <CtaButton
                loading={processing}
                disabled={processing || !isFormValid}
                showArrow
                onClick={handleSubscribe}
              >
                {getSubscribeButtonText()}
              </CtaButton>
            </CtaSection>
          </ContentBottom>
        )}
      </PaymentContent>
    </AccountLayout>
  );
});
